import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import styled from 'styled-components';
import OcInput from '../Input/OcInput';
import appTheme from '../../theme/appTheme';
import { SystemIconButton } from '../Icon/SystemIconButton';
import { Uds } from '../../types';

export type CounterProps = Uds<{
  initialValue: number;
  step?: number;
  min?: number;
  max?: number;
  onChange?(value: number): void;
  disabled?: boolean;
  className?: string;
  size?: 'small' | 'medium';
}>;

export const Counter: FC<React.PropsWithChildren<CounterProps>> = ({
  initialValue,
  step = 1,
  min,
  max,
  onChange,
  disabled,
  dataTest = 'counter-container',
  className,
  size = 'medium'
}) => {
  const [value, setValue] = useState<number>(initialValue);
  const ensuredMin = Number(min); // on undefined/null will give NaN
  const ensuredMax = Number(max);

  // Calculate decimal places for dynamic component width and setValueRound function
  const decimalPlaces = useMemo(() => countDecimals(step), [step]);

  // Add extra space if decimal places introduces
  const componentWidth = (size === 'medium' ? 150 : 107) + decimalPlaces * 10;

  // Round correctly number. Fix JS floating number calculation
  const setValueRound = useCallback(
    (value: number) => setValue(parseFloat(value.toFixed(decimalPlaces))),
    [decimalPlaces]
  );

  const onDecrement = () => {
    if (min !== undefined && min !== null && value <= min) {
      return;
    }
    if (value - step < ensuredMin) {
      setValueRound(ensuredMin);
    } else {
      setValueRound(value - step);
    }
  };

  const onIncrement = () => {
    if (max !== undefined && max !== null && value >= max) {
      return;
    }
    if (value + step > ensuredMax) {
      setValueRound(ensuredMax);
    } else {
      setValueRound(value + step);
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    if (newValue > ensuredMax) {
      setValueRound(ensuredMax);
    } else if (newValue < ensuredMin) {
      setValueRound(ensuredMin);
    } else {
      setValueRound(newValue);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    onChange?.(value);
  }, [value]);

  // Allow component to reinitialize value when initialValue changes.
  useEffect(() => {
    setValueRound(initialValue);
  }, [initialValue, setValueRound]);

  return (
    <StyledButtonGroup
      className={className}
      disableElevation
      variant="contained"
      width={componentWidth}
      data-test={dataTest}
      size={size}
    >
      <SystemIconButton
        iconName="remove"
        onClick={onDecrement}
        disabled={disabled}
        dataTest="counter-decrease-button"
      />
      <StyledInputWrapper $size={size}>
        <OcInput
          value={value !== null ? value.toString() : '0'}
          type="number"
          onChange={onInputChange}
          disabled={disabled}
          disableUnderline
          dataTest="counter-input"
        />
      </StyledInputWrapper>
      <SystemIconButton iconName="add" onClick={onIncrement} disabled={disabled} dataTest="counter-increase-button" />
    </StyledButtonGroup>
  );
};

export default Counter;

const countDecimals = (value: number): number =>
  Math.floor(value) === value ? 0 : value.toString().split('.')[1]?.length || 0;

const StyledButtonGroup = styled(ButtonGroup)<{ size: string; width: number }>`
  height: ${({ size }) => (size === 'medium' ? 48 : 34)}px;
  width: ${({ width }) => width}px;
  border: 1px solid ${(props) => props.theme.colorSurfaceVariant};
  border-radius: 4px;

  button {
    width: ${({ size }) => (size === 'medium' ? 50 : 32)}px;
    background-color: ${(props) => props.theme.colorSurface};
    color: ${(props) => props.theme.colorOnSurface};
  }

  .MuiButtonGroup-groupedContainedHorizontal:not(:last-child) {
    border-color: ${(props) => props.theme.colorSurfaceVariant};
  }

  .MuiButtonGroup-grouped {
    min-width: ${({ size }) => (size === 'medium' ? 40 : 32)}px;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }
`;

const StyledInputWrapper = styled.div<{ $size: string }>`
  height: ${({ $size }) => ($size === 'medium' ? 47 : 32)}px;

  .MuiInput-root {
    height: ${({ $size }) => ($size === 'medium' ? 'auto' : '32px')};
    padding: ${appTheme.spacingSize.m};

    .MuiInputBase-input {
      // Overwrite padding to fit input in ButtonGroup
      padding: 6px 0 5px;
      font-size: ${appTheme.font.sizeSmall};
      text-align: center;
      min-width: 100%;
    }

    &.Mui-focused,
    &:hover {
      border-radius: 0;
    }
  }
`;
